<template>
  <EntityListItem
    :icon="task.completed_at || task.status === 'completed' ? 'check-square' : 'circle-dot'"
    :icon-tone="iconTone"
    :title="task.title"
    :tone="isOverdue ? 'danger' : 'default'"
    :muted="task.status === 'completed'"
    clickable
    @click="handleClick"
  >
    <template #title-meta>
      <span
        v-if="task.priority && task.priority !== 'low'"
        class="task-list-item__priority"
        :class="`task-list-item__priority--${task.priority}`"
      >
        {{ priorityLabel }}
      </span>
    </template>

    <template #subtitle>
      <span v-if="relatedLabel" class="task-list-item__related">
        <BaseIcon :name="relatedIcon" size="11" />
        {{ relatedLabel }}
      </span>
    </template>

    <template #right>
      <span
        class="task-list-item__when"
        :class="{ 'task-list-item__when--overdue': isOverdue }"
      >
        <BaseIcon name="clock" size="11" />
        {{ dueText }}
      </span>
    </template>
  </EntityListItem>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import dayjs from 'dayjs';
  import EntityListItem from '@/components/partials/lists/EntityListItem.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { useTaskDetail } from '@/composables/useTaskDetail';

  export default {
    name: 'TaskListItem',

    components: { EntityListItem, BaseIcon },

    props: {
      task: { type: Object, required: true },
      /** Open TaskDetailModal on click (set false when parent handles the click). */
      openDetailOnClick: { type: Boolean, default: true },
      /** @deprecated use openDetailOnClick — kept so existing :navigate-on-click="false" still works */
      navigateOnClick: { type: Boolean, default: undefined },
    },

    emits: ['click', 'updated'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const { openTaskDetail } = useTaskDetail();

      const dueDayjs = computed(() => {
        if (!props.task.due_date) return null;
        return dayjs(`${props.task.due_date}T${props.task.due_time || '23:59:59'}`);
      });

      const isOverdue = computed(() => {
        if (props.task.status === 'completed') return false;
        return dueDayjs.value ? dueDayjs.value.isBefore(dayjs()) : false;
      });

      const iconTone = computed(() => {
        if (props.task.status === 'completed') return 'green';
        return isOverdue.value ? 'red' : 'brand';
      });

      const dueText = computed(() => {
        if (!props.task.due_date) return $t('home.no_due_date');
        const due = dueDayjs.value;
        const today = dayjs().startOf('day');
        const tomorrow = today.add(1, 'day');
        const dueDay = due.startOf('day');
        const time = props.task.due_time ? due.format('HH:mm') : '';
        if (dueDay.isSame(today)) return time ? `${$t('home.today')} ${time}` : $t('home.today');
        if (dueDay.isSame(tomorrow)) {
          return time ? `${$t('home.tomorrow')} ${time}` : $t('home.tomorrow');
        }
        return time ? due.format('DD MMM HH:mm') : due.format('DD MMM');
      });

      const priorityLabel = computed(() => {
        const map = {
          high: $t('deals.priority_high'),
          medium: $t('deals.priority_medium'),
          low: $t('deals.priority_low'),
        };
        return map[props.task.priority] || props.task.priority;
      });

      const homeLabel = computed(() => {
        if (props.task.project_id || props.task.project?.id) {
          const number = props.task.project?.project_number;
          const title = props.task.project?.title || props.task.lead?.name || '';
          const wo = props.task.work_order?.work_order_number;
          const base = number ? `${number}${title ? ` · ${title}` : ''}` : title;
          return wo ? `${$t('tasks_view.home_project')} · ${base || wo}` : `${$t('tasks_view.home_project')}${base ? ` · ${base}` : ''}`;
        }
        if (props.task.deal?.title || props.task.deal_id) {
          return `${$t('tasks_view.home_deal')}${props.task.deal?.title ? ` · ${props.task.deal.title}` : ''}`;
        }
        if (props.task.lead?.name) {
          return props.task.lead.name;
        }
        return $t('tasks_view.home_general');
      });

      const relatedLabel = computed(() => homeLabel.value);

      const relatedIcon = computed(() => {
        if (props.task.project_id || props.task.project?.id) return 'clipboard';
        if (props.task.deal?.title || props.task.deal_id) return 'crm';
        return 'user';
      });

      const shouldOpenDetail = computed(() => {
        if (props.navigateOnClick !== undefined) return props.navigateOnClick;
        return props.openDetailOnClick;
      });

      const handleClick = () => {
        emit('click', props.task);
        if (!shouldOpenDetail.value) return;
        openTaskDetail(props.task, {
          onUpdated: (updated) => emit('updated', updated || props.task),
        });
      };

      return {
        isOverdue,
        iconTone,
        dueText,
        priorityLabel,
        relatedLabel,
        relatedIcon,
        handleClick,
      };
    },
  };
</script>

<style scoped lang="scss">
  .task-list-item {
    &__priority {
      display: inline-flex;
      align-items: center;
      padding: 0.0625rem 0.375rem;
      border-radius: 0.25rem;
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.025em;
      flex-shrink: 0;

      &--high {
        background: var(--red-50);
        color: var(--red-700);
      }

      &--medium {
        background: var(--amber-50);
        color: var(--amber-700);
      }
    }

    &__related {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
    }

    &__when {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.75rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
      white-space: nowrap;

      .icon {
        color: var(--slate-400);
      }

      &--overdue {
        color: var(--red-600);
        font-weight: 500;
        .icon {
          color: var(--red-500);
        }
      }
    }
  }
</style>
